// pages/AdminOrders.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom"; // ⬅️ for back navigation
import * as api from "../../api/index"; // your API layer

const AdminOrders = () => {
  const navigate = useNavigate(); // ⬅️ initialize navigation

  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState(""); // all by default
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [acceptedOrders, setAcceptedOrders] = useState([]); // track accepted IDs

  const fetchOrders = async (pageNumber, status) => {
    try {
      setLoading(true);
      setError("");
      const res = await api.getAdminOrder(pageNumber, 20, status);
      if (res.data.success) {
        setOrders(res.data.orders || []);
        setTotalPages(res.data.pages || 1);
      } else {
        setError("Failed to load orders.");
      }
    } catch (err) {
    
      setError("⚠️ Could not load orders. Try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders(page, statusFilter);
  }, [page, statusFilter]);


  const handleAccept = async (orderId) => {
    try {
      await api.acceptOrders(orderId);
      setAcceptedOrders((prev) => [...prev, orderId]);
      fetchOrders(page, statusFilter);
    
    } catch (err) {
    
      alert("Failed to accept order.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="mb-4 px-4 py-2 rounded-lg transition"
      >
        ← Back
      </button>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 text-center">
        Admin - All Orders
      </h1>

      {/* Filters */}
      <div className="flex justify-center gap-4 mb-6">
        {["", "pending", "current", "completed"].map((status) => (
          <button
            key={status || "all"}
            onClick={() => {
              setStatusFilter(status);
              setPage(1);
            }}
            className={`px-4 py-2 rounded-lg font-medium ${
              statusFilter === status
                ? "bg-orange-500 text-white"
                : "bg-gray-200 hover:bg-gray-300"
            }`}
          >
            {status === "" ? "All" : status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-center text-gray-600">Loading orders...</p>
      ) : error ? (
        <p className="text-center text-red-500">{error}</p>
      ) : orders.length === 0 ? (
        <p className="text-center text-gray-500">No orders found.</p>
      ) : (
        <>
          {/* Orders List */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {orders.map((order) => (
              <div
                key={order._id}
                className="bg-white rounded-xl shadow-md p-5 hover:shadow-lg transition"
              >
                <div className="flex justify-between items-center mb-3">
                  <h2 className="text-lg font-semibold text-gray-800">
                    {order.item}
                  </h2>
                  <span
                    className={`px-3 py-1 text-xs font-medium rounded-full ${
                      order.status === "pending"
                        ? "bg-yellow-100 text-yellow-700"
                        : order.status === "current"
                        ? "bg-blue-100 text-blue-700"
                        : "bg-green-100 text-green-700"
                    }`}
                  >
                    {order.status}
                  </span>
                </div>

                <p className="text-gray-600 text-sm mb-2">{order.description}</p>
                <p className="text-sm text-gray-500">
                  Amount: <span className="font-medium">₦{order.amount}</span>
                </p>
                <p className="text-sm text-gray-500">
                  Delivery: {order.deliveryAddress}
                </p>

                {/* Buyer & Seller Info */}
                <div className="text-sm text-gray-700 mt-2 border-t pt-2">
                  <p>
                    <span className="font-semibold">Buyer:</span>{" "}
                    {order.buyer?.Name || "N/A"} ({order.buyer?.Email || "N/A"})
                  </p>
                  <p>
                    <span className="font-semibold">Seller:</span>{" "}
                    {order.seller?.Name || "N/A"} ({order.seller?.Email || "N/A"})
                  </p>
                </div>

                <p className="text-xs text-gray-400 mt-2">
                  Created:{" "}
                  {order.createdAt
                    ? new Date(order.createdAt).toLocaleDateString()
                    : "N/A"}
                </p>

                {/* Accept Button */}
                {order.status === "pending" && (
                  <button
                    onClick={() => handleAccept(order._id)}
                    disabled={acceptedOrders.includes(order._id)}
                    className={`mt-3 px-4 py-2 rounded-lg transition ${
                      acceptedOrders.includes(order._id)
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-green-500 text-white hover:bg-green-600"
                    }`}
                  >
                    {acceptedOrders.includes(order._id)
                      ? "Accepted"
                      : "Accept Order"}
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          <div className="flex justify-center items-center gap-4 mt-8">
            <button
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              disabled={page === 1}
              className="px-4 py-2 bg-gray-300 rounded-lg hover:bg-gray-400 disabled:opacity-50"
            >
              Prev
            </button>
            <span className="text-gray-700 font-medium">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={page === totalPages}
              className="px-4 py-2 bg-gray-300 rounded-lg hover:bg-gray-400 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminOrders;
